import React from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import Navbar from '../../../shared/components/Navbar';
import Footer from '../../../shared/components/Footer';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import Card from '../../../shared/components/Card';

const ConditionsUtilisation: React.FC = () => {
  const { t } = usePageTranslation('public', 'ConditionsUtilisation');

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <Navbar />
      <div className="relative z-10">
        {/* Header de la page */}
        <section className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 text-gradient">
                {t('title')}
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {t('subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contenu principal */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-blue-500/20 text-blue-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-blue-500/50">
                      1
                    </span>
                    {t('acceptance.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('acceptance.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-green-500/20 text-green-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-green-500/50">
                      2
                    </span>
                    {t('service.title')}
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    {t('service.description')}
                  </p>
                  <ul className="space-y-2">
                    {t('service.items').map((item: string, index: number) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <span className="text-green-400 font-bold mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-yellow-500/20 text-yellow-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-yellow-500/50">
                      3
                    </span>
                    {t('account.title')}
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">{t('account.description')}</p>
                  <ul className="space-y-2">
                    {t('account.items').map((item: string, index: number) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <span className="text-yellow-400 font-bold mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-red-500/20 text-red-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-red-500/50">
                      4
                    </span>
                    {t('invitation.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('invitation.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-purple-500/20 text-purple-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-purple-500/50">
                      5
                    </span>
                    {t('conduct.title')}
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">{t('conduct.description')}</p>
                  <ul className="space-y-2">
                    {t('conduct.items').map((item: string, index: number) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <span className="text-purple-400 font-bold mr-2">•</span>
                        {item.includes('Règlement') ? (
                          <>
                            {item.split('Règlement')[0]}
                            <a href="/legal/reglement" className="text-primary-400 hover:text-primary-300 underline">
                              Règlement
                            </a>
                            {item.split('Règlement')[1]}
                          </>
                        ) : (
                          item
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-indigo-500/20 text-indigo-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-indigo-500/50">
                      6
                    </span>
                    {t('content.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('content.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-pink-500/20 text-pink-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-pink-500/50">
                      7
                    </span>
                    {t('intellectual.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('intellectual.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-orange-500/20 text-orange-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-orange-500/50">
                      8
                    </span>
                    {t('liability.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('liability.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-teal-500/20 text-teal-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-teal-500/50">
                      9
                    </span>
                    {t('modifications.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('modifications.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-cyan-500/20 text-cyan-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-cyan-500/50">
                      10
                    </span>
                    {t('termination.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('termination.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-emerald-500/20 text-emerald-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-emerald-500/50">
                      11
                    </span>
                    {t('changes.title')}
                  </h2>
                  <p className="text-lg text-gray-300">
                    {t('changes.description')}
                  </p>
                </div>

                <div className="legal-section mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-violet-500/20 text-violet-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-violet-500/50">
                      12
                    </span>
                    {t('contact.title')}
                  </h2>
                  <p className="text-lg text-gray-300 mb-4">
                    {t('contact.description')}
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start text-gray-300">
                      <span className="text-violet-400 font-bold mr-2">•</span>
                      <strong>{t('contact.discord')} :</strong> {t('contact.discordAction')}
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="text-violet-400 font-bold mr-2">•</span>
                      <strong>{t('contact.email')} :</strong>{' '}
                      <a href="mailto:support@opsecours.fr" className="text-primary-400 hover:text-primary-300 underline">
                        support@opsecours.fr
                      </a>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="text-violet-400 font-bold mr-2">•</span>
                      <strong>{t('contact.owner')} :</strong> Bondi51
                    </li>
                  </ul>
                  <p className="text-lg text-gray-300">
                    <strong>{t('contact.note')}</strong>
                  </p>
                </div>

                <div className="mt-12 p-6 bg-gradient-to-r from-gray-800/50 to-blue-900/50 rounded-xl border-l-4 border-blue-500">
                  <p className="text-lg font-semibold text-white">
                    <strong>{t('lastUpdate')} :</strong> 01/07/2024
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default ConditionsUtilisation;

